import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Actions,
  addTermLoanPayment,
  getTransformedRepaymentScheduleByLoanId
} from "../../actions/loan.action";
import Badge from "../../components/badge";
import Button from "../../components/button";
import ConfirmDialog, {
  ConfirmDialogType
} from "../../components/confirm-dialog";
import Table, { TableColumns } from "../../components/table";
import { RepaymentSchedule } from "../../dto";
import { ReactComponent as DownloadIcon } from "../../images/download.svg";
import dispatch from "../../middleware";
import {
  AddTermLoanPayment,
  LoanRepaymentScheduleStatusEnum,
  TransactionMediumEnum
} from "../../models";
import { ReduxState } from "../../reducers";
import {
  clearFormData,
  dateToDDMMYYYY,
  downloadCSV,
  getTodayDateForInputField,
  isLoadingActive,
  splitKeyWithSpace
} from "../../utils";
import { validateAddTermLoanPaymentFields } from "../loan-details/helpers";

interface LoanRepaymentScheduleProps {
  loanId: string;
  disbursed?: boolean;
}

const COLUMNS_FOR_LOAN_REPAYMENT_SCHEDULE: TableColumns[] = [
  {
    field: "emiNumber",
    label: "Installment Number",
    style: { textAlign: "center" },
  },
  { field: "dueDate", label: "Due Date", style: { textAlign: "center" } },
  { field: "amount", label: "Amount", style: { textAlign: "center" } },
  { field: "principal", label: "Principal", style: { textAlign: "center" } },
  { field: "interest", label: "Interest", style: { textAlign: "center" } },
  {
    field: "principalRepaidAmount",
    label: "Principal Paid",
    style: { textAlign: "center" },
  },
  {
    field: "interestRepaidAmount",
    label: "Interest Paid",
    style: { textAlign: "center" },
  },
  { field: "status", label: "Status", style: { textAlign: "center" } },
];

const getTableDataForLoanRepaymentSchedule = (
  repaymentSchedule: RepaymentSchedule[],
) => {
  var emiNumber = 0
  const allHaveDueDate = repaymentSchedule.every(
    (item) => item.dueDate !== undefined && item.dueDate !== null && item.dueDate !== ''
  );
  const sortedRepaymentSchedule = allHaveDueDate
  ? [...repaymentSchedule].sort((a, b) => a.dueDate!.localeCompare(b.dueDate!))
  : repaymentSchedule;
  return sortedRepaymentSchedule.map((data, index) => {
    const {
      dueDate,
      interest,
      principal,
      status,
      principalRepaidAmount,
      interestRepaidAmount,
      isPrepayment

    } = data;
    if(!isPrepayment){
      emiNumber = emiNumber+1
    }
    return {
      emiNumber: isPrepayment?"-": emiNumber ,
      dueDate: dateToDDMMYYYY(dueDate),
      interest: interest.toFixed(2),
      principal: principal.toFixed(2),
      amount: (principal + interest).toFixed(2),
      principalRepaidAmount: principalRepaidAmount.toFixed(2),
      interestRepaidAmount: interestRepaidAmount.toFixed(2),
      status: isPrepayment? getStatusBadge(LoanRepaymentScheduleStatusEnum.PRE_PAYMENT):getStatusBadge(status),
    };
  });
};

function getStatusBadge(status: LoanRepaymentScheduleStatusEnum) {
  if (status === LoanRepaymentScheduleStatusEnum.PAID)
    return <Badge type="success">Repaid</Badge>;
  if (status === LoanRepaymentScheduleStatusEnum.PRE_PAYMENT)
    return <Badge type="success">Prepaid</Badge>;
  if (status === LoanRepaymentScheduleStatusEnum.PARTIALLY_PAID)
    return <Badge type="pending"></Badge>;
  return <Badge type="pending">Due</Badge>;
}

export default function LoanRepaymentSchedule({
  loanId,
  disbursed = false,
}: LoanRepaymentScheduleProps) {
  const storeDispatch = useDispatch();
  const repaymentSchedule: RepaymentSchedule[] = useSelector(
    (state: ReduxState) => state.loan.singleLoanRepaymentSchedule,
  );
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue,
  );
  const [isModalActive, setIsModalActive] = useState({
    acceptEMI: false,
  });

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_TRANSFORMED_REPAYMENT_SCHEDULE_BY_LOAN_ID,
  ]);

  useEffect(() => {
    if (!loanId) return;
    dispatch(storeDispatch, getTransformedRepaymentScheduleByLoanId(loanId));
  }, [loanId, storeDispatch]);

  async function handleConfirmDialog(type: ConfirmDialogType, fieldData: any) {
    validateAddTermLoanPaymentFields(fieldData);

    const payload: AddTermLoanPayment = {
      amount: parseInt(fieldData.amount),
      transactionRefNo: fieldData.transactionRefNo,
      paidAt: fieldData.paidAt,
      medium: fieldData.medium,
    };
    const response = await dispatch(
      storeDispatch,
      addTermLoanPayment(clearFormData(payload), loanId),
    );
    // Reload the data after EMI capture
    dispatch(storeDispatch, getTransformedRepaymentScheduleByLoanId(loanId));
    return {
      message: `Payment of Rs ${fieldData.amount} for Loan # ${loanId} added successfully with Transaction ID: ${response?._id}`,
    };
  }

  const tableDataForLoanRepaymentSchedule = useMemo(() => {
    if (!repaymentSchedule.length) return [];
    return getTableDataForLoanRepaymentSchedule(repaymentSchedule);
  }, [repaymentSchedule]);

  return (
    <>
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="user-page-title mb-4">Repayment Schedule</h3>
          {disbursed && (
            <div>
            </div>
          )}
        </div>
      </div>
      <Table
        loading={isLoading}
        columns={COLUMNS_FOR_LOAN_REPAYMENT_SCHEDULE}
        data={tableDataForLoanRepaymentSchedule}
      />
      <ConfirmDialog
        active={isModalActive.acceptEMI}
        type="success"
        title="Add EMI Payment"
        message="Please fill the below details to add payment"
        onCancel={() =>
          setIsModalActive((prevState) => ({ ...prevState, acceptEMI: false }))
        }
        onSubmit={handleConfirmDialog}
        submitButtonText="Add"
        cancelButtonText="Cancel"
        inputFields={[
          {
            name: "amount",
            label: "Amount",
            type: "number",
            placeholder: "amount",
            required: true,
          },
          {
            name: "transactionRefNo",
            label: "Transaction Ref No",
            type: "text",
            placeholder: "refno",
            required: true,
          },
          {
            name: "paidAt",
            label: "Paid On",
            type: "date",
            placeholder: "YYYYMMDD",
            value: getTodayDateForInputField(),
            required: true,
          },
        ]}
        selectFields={[
          {
            name: "medium",
            label: "Transaction Medium",
            options: Object.values(TransactionMediumEnum).map((value) => ({
              label: splitKeyWithSpace(value),
              value,
            })),
            required: true,
          },
        ]}
      />
    </>
  );
}
